import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import { usePlayerSelection } from "@/hooks/usePlayerSelection";
import { usePlayerDeletion } from "@/hooks/usePlayerDeletion";
import { Player } from "@/lib/types";
import { lazy, Suspense } from "react";
import PlayerEmailModal from "./PlayerEmailModal";

// Dynamically import heavy components
const PlayerDetailModal = lazy(() => import("./PlayerDetailModal"));
import BulkActionsBar from "./BulkActionsBar";
import { PlayerDeletionDialog } from "./PlayerDeletionDialog";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { useUnitsPreference } from "@/hooks/useUnitsPreference";
import { convertHeight, convertWeight } from "@/lib/unitConversion";
import { PlayerTableSkeleton } from "@/components/ui/SkeletonLoader";
import { exportPlayersToCSV, generateCSVFilename, type ColumnConfig } from "@/lib/csvExport";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Column configuration using shared interface
const AVAILABLE_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', defaultVisible: true, alwaysVisible: true },
  { key: 'age', label: 'Age', defaultVisible: true },
  { key: 'position', label: 'Position', defaultVisible: true },
  { key: 'specificPositions', label: 'Specific Positions', defaultVisible: false },
  { key: 'height', label: 'Height', defaultVisible: true },
  { key: 'weight', label: 'Weight', defaultVisible: true },
  { key: 'playingLevel', label: 'Level', defaultVisible: true },
  { key: 'gpa', label: 'GPA', defaultVisible: false },
  { key: 'hsGradYear', label: 'HS Grad Year', defaultVisible: false },
  { key: 'currentClub', label: 'Current Club', defaultVisible: false },
  { key: 'nationality', label: 'Nationality', defaultVisible: true },
  { key: 'internationalCaps', label: 'Int\'l Caps', defaultVisible: false },
  { key: 'previousClubs', label: 'Previous Clubs', defaultVisible: false },
  { key: 'universityGradYear', label: 'University Grad Year', defaultVisible: false },
  { key: 'jerseyNumber', label: 'Jersey Number', defaultVisible: false },
  { key: 'hsGradYearInEmail', label: 'HS Grad Year in Email', defaultVisible: false },
  { key: 'universityGradYearInEmail', label: 'Univ Grad Year in Email', defaultVisible: false },
  { key: 'event', label: 'Event(s)', defaultVisible: true },
];

interface PlayerTableProps {
  filters: Record<string, string>;
  includeMissingValues: boolean;
  onFilteredCountChange?: (count: number) => void;
  onFilterRemoval?: (filtersToRemove: string[]) => void;
}

export default function PlayerTable({ 
  filters, 
  includeMissingValues = true,
  onFilteredCountChange,
  onFilterRemoval
}: PlayerTableProps) {
  // State for shortlist menu and email modal (must be at the top of the function)
  const [shortlistMenuOpen, setShortlistMenuOpen] = useState<number | null>(null);
  const [emailModalPlayer, setEmailModalPlayer] = useState<Player | null>(null);
  const shortlistMenuRef = useRef<HTMLDivElement | null>(null);

  // Add state for single-player add to shortlist dialog
  const [singleAddToShortlistPlayer, setSingleAddToShortlistPlayer] = useState<Player | null>(null);
  
  const { toast } = useToast();
  
  // Use data coordinator for better loading management
  const { players: allPlayers, shortlists, isInitialLoading } = useDataCoordinator();
  
  // Player deletion hook
  const { removePlayer, isRemoving } = usePlayerDeletion();
  
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Deletion dialog state
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Use shared player selection hook
  const {
    selectedPlayerIds,
    handleSelectPlayer,
    handleSelectAll,
    handleSelectAllFiltered,
    handleClearSelection,
    isPlayerSelected,
    areAllSelected,
    areSomeSelected,
    getSelectionCount
  } = usePlayerSelection({
    clearOnChange: [JSON.stringify(filters), includeMissingValues]
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [JSON.stringify(filters), includeMissingValues]);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('playerTableColumns');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        // Fall back to defaults if parsing fails
      }
    }
    return new Set(AVAILABLE_COLUMNS.filter(col => col.defaultVisible).map(col => col.key));
  });
  
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  
  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem('playerTableColumns', JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);
  
  // Handle scroll indicators
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const leftShadow = document.getElementById('scroll-shadow-left');
      const rightShadow = document.getElementById('scroll-shadow-right');
      
      if (leftShadow && rightShadow) {
        const scrollLeft = target.scrollLeft;
        const maxScrollLeft = target.scrollWidth - target.clientWidth;
        
        // Show left shadow when scrolled right
        leftShadow.style.opacity = scrollLeft > 0 ? '1' : '0';
        
        // Show right shadow when not fully scrolled right
        rightShadow.style.opacity = scrollLeft < maxScrollLeft ? '1' : '0';
      }
    };
    
    const scrollContainer = document.querySelector('.overflow-x-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Initial check
      const initialEvent = { target: scrollContainer } as unknown as Event;
      handleScroll(initialEvent);
      
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [allPlayers, filters, includeMissingValues]); // Re-run when players or filters change
  
  const playersPerPage = 10;
  
  // Filter players based on filter criteria - memoized for performance
  const filterPlayers = useCallback((players: Player[]) => {
    return players.filter(player => {
      // Check for missing values only in actively filtered fields
      if (!includeMissingValues) {
        // Only check for missing values in fields that are actively filtered
        if (filters && Object.keys(filters).length > 0) {
          // Check missing values only for actively filtered fields
          if ((filters.position || filters.positions) && (!player.position || player.position.length === 0)) return false;
          if ((filters.nationality || filters.nationalities) && !player.nationality) return false;
          if ((filters.playing_level || filters.playingLevels) && !player.playing_level) return false;
          if ((filters.event || filters.events) && (!player.event || player.event.length === 0)) return false;
          if (filters.current_club && !player.current_club) return false;
          if ((filters.heightMin || filters.heightMax) && !player.height) return false;
          if ((filters.weightMin || filters.weightMax) && !player.weight) return false;
          if ((filters.gpaMin || filters.gpaMax) && !player.gpa) return false;
          if ((filters.ageMin || filters.ageMax) && (player.age === null || player.age === undefined)) return false;
          if ((filters.hsGradYearMin || filters.hsGradYearMax) && !player.hs_grad_year) return false;
          if (filters.specificPositions && (!player.specific_positions || player.specific_positions.length === 0)) return false;
          if (filters.internationalCaps && filters.internationalCaps !== "any") {
            const hasCapsData = (player.international_caps && Array.isArray(player.international_caps) && player.international_caps.length > 0) || 
                               (player.distinguishments && Array.isArray(player.distinguishments) && player.distinguishments.length > 0);
            if (!hasCapsData) return false;
          }
        }
        // When no filters are active, "Exclude missing values" has no effect
        // This prevents the issue where all players are filtered out when no filters are applied
      }
      
      // If no other filters are applied, return true (player passes missing values check above)
      if (!filters || Object.keys(filters).length === 0) return true;
      
      // Basic text-based filters
      if (filters.name && !player.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }
      
      if (filters.current_club && player.current_club && !player.current_club.toLowerCase().includes(filters.current_club.toLowerCase())) {
        return false;
      }
      
      // Position filtering - handle both single position and multiple positions
      if (filters.position) {
        if (!Array.isArray(player.position) || player.position.length === 0) return false;
        const hasMatch = player.position.some(pos => typeof pos === 'string' && pos.toLowerCase().includes(filters.position.toLowerCase()));
        if (!hasMatch) return false;
      } else if (filters.positions) {
        if (!Array.isArray(player.position) || player.position.length === 0) return false;
        const positionsArray = filters.positions.split(',').map(p => p.trim());
        const hasMatch = positionsArray.some(filterPos =>
          player.position && player.position.some((playerPos: string) =>
            typeof playerPos === 'string' && playerPos.toLowerCase().includes(filterPos.toLowerCase())
          )
        );
        if (!hasMatch) return false;
      }
      // Nationality filtering - handle both single and multiple
      if (filters.nationality) {
        if (!player.nationality || player.nationality.toLowerCase() === 'n/a') return false;
        if (!player.nationality.toLowerCase().includes(filters.nationality.toLowerCase())) {
          return false;
        }
      } else if (filters.nationalities) {
        if (!player.nationality || player.nationality.toLowerCase() === 'n/a') return false;
        const nationalitiesArray = filters.nationalities.split(',').map(n => n.trim());
        const hasMatch = nationalitiesArray.some(nat => 
          player.nationality && player.nationality.toLowerCase().includes(nat.toLowerCase())
        );
        if (!hasMatch) {
          return false;
        }
      }
      // Playing level filtering - handle both single and multiple
      if (filters.playing_level) {
        if (!player.playing_level || player.playing_level.trim().toLowerCase() === 'n/a') return false;
        if (!player.playing_level.toLowerCase().includes(filters.playing_level.toLowerCase())) {
          return false;
        }
      } else if (filters.playingLevels) {
        if (!player.playing_level || player.playing_level.trim().toLowerCase() === 'n/a') return false;
        const levelsArray = filters.playingLevels.split(',').map(l => l.trim());
        const hasMatch = levelsArray.some(level => 
          player.playing_level && player.playing_level.toLowerCase().includes(level.toLowerCase())
        );
        if (!hasMatch) {
          return false;
        }
      }

      // Event filtering - mirror specific position logic
      if (filters.event && Array.isArray(player.event) && player.event.length > 0) {
        const filterValue = filters.event.toLowerCase();
        const hasMatch = player.event.some(ev => typeof ev === 'string' && ev.toLowerCase().includes(filterValue));
        if (!hasMatch) return false;
      } else if (filters.events && Array.isArray(player.event) && player.event.length > 0) {
        let eventsArray: string[] = [];
        if (Array.isArray(filters.events)) {
          eventsArray = filters.events.map((e: string) => e.toLowerCase());
        } else if (typeof filters.events === 'string') {
          eventsArray = filters.events.split(',').map((e: string) => e.trim().toLowerCase()).filter(Boolean);
        }
        const hasMatch = eventsArray.some(filterEvent =>
          player.event && player.event.some((playerEvent: string) =>
            typeof playerEvent === 'string' && playerEvent.toLowerCase().includes(filterEvent)
          )
        );
        if (!hasMatch) return false;
      } else if ((filters.event || filters.events)) {
        // If an event filter is set but player has no events, exclude
        return false;
      }
      
      // Age filtering - handle individual min/max values
      if ((filters.ageMin || filters.ageMax) && player.age !== null && player.age !== undefined) {
        if (filters.ageMin) {
          const minAge = parseInt(filters.ageMin);
          if (player.age < minAge) return false;
        }
        
        if (filters.ageMax) {
          const maxAge = parseInt(filters.ageMax);
          if (player.age > maxAge) return false;
        }
      }
      
      // Handle height filtering - database stores metric format (cm)
      if ((filters.heightMin || filters.heightMax) && player.height) {
        // Extract height value in cm from database format (e.g., "185 cm")
        const heightCm = parseInt(player.height.replace(/[^\d]/g, '')) || 0;
        
        // Convert to filter unit for comparison
        let playerHeight = heightCm;
        if (filters.heightUnit === "imperial") {
          // Convert cm to inches for imperial filtering
          playerHeight = Math.round(heightCm / 2.54);
        }
        
        if (filters.heightMin) {
          const minHeight = parseInt(filters.heightMin);
          if (playerHeight < minHeight) return false;
        }
        
        if (filters.heightMax) {
          const maxHeight = parseInt(filters.heightMax);
          if (playerHeight > maxHeight) return false;
        }
      }
      
      // Handle weight filtering - database stores metric format (kg)
      if ((filters.weightMin || filters.weightMax) && player.weight) {
        // Extract weight value in kg from database format (e.g., "75 kg")
        const weightKg = parseInt(player.weight.replace(/[^\d]/g, '')) || 0;
        
        // Convert to filter unit for comparison
        let playerWeight = weightKg;
        if (filters.weightUnit === "imperial") {
          // Convert kg to lbs for imperial filtering
          playerWeight = Math.round(weightKg / 0.453592);
        }
        
        if (filters.weightMin) {
          const minWeight = parseInt(filters.weightMin);
          if (playerWeight < minWeight) return false;
        }
        
        if (filters.weightMax) {
          const maxWeight = parseInt(filters.weightMax);
          if (playerWeight > maxWeight) return false;
        }
      }
      
      // GPA range filtering
      if ((filters.gpaMin || filters.gpaMax) && player.gpa) {
        const gpa = parseFloat(player.gpa);
        
        if (filters.gpaMin) {
          const minGpa = parseFloat(filters.gpaMin);
          if (gpa < minGpa) return false;
        }
        
        if (filters.gpaMax) {
          const maxGpa = parseFloat(filters.gpaMax);
          if (gpa > maxGpa) return false;
        }
      }
      
      // High school graduation year filtering
      if ((filters.hsGradYearMin || filters.hsGradYearMax) && player.hs_grad_year) {
        if (filters.hsGradYearMin) {
          const minYear = parseInt(filters.hsGradYearMin);
          if (player.hs_grad_year < minYear) return false;
        }
        
        if (filters.hsGradYearMax) {
          const maxYear = parseInt(filters.hsGradYearMax);
          if (player.hs_grad_year > maxYear) return false;
        }
      }
      
      // International caps filtering
      if (filters.internationalCaps && filters.internationalCaps !== "any") {
        // Check if player has international caps in the dedicated field
        const hasInternationalCaps = player.international_caps && 
                                    Array.isArray(player.international_caps) && 
                                    player.international_caps.length > 0;
        
        // Fallback to checking distinguishments field for backward compatibility
        const hasInternationalMention = player.distinguishments?.some(d => 
          d.toLowerCase().includes("national team") || d.toLowerCase().includes("international")
        );
        
        const hasAnyInternationalExperience = hasInternationalCaps || hasInternationalMention;
        
        if (filters.internationalCaps === "yes" && !hasAnyInternationalExperience) {
          return false;
        }
        
        if (filters.internationalCaps === "no" && hasAnyInternationalExperience) {
          return false;
        }
      }
      
      // Specific Position filtering
      if (filters.specificPositions) {
        const specificPositionsList = filters.specificPositions.split(',').map(sp => sp.trim());
        
        // Skip if player has no specific positions defined
        if (!player.specific_positions || !Array.isArray(player.specific_positions) || player.specific_positions.length === 0) {
          return false;
        }
        
        // Check if any of the player's specific positions match any of the filter specific positions
        const positionMatches = specificPositionsList.some(specificPos => 
          player.specific_positions!.some(playerPos => 
            playerPos.toLowerCase().includes(specificPos.toLowerCase())
          )
        );
        
        if (!positionMatches) return false;
      }
      
      return true;
    });
  }, [filters, includeMissingValues, visibleColumns]);
  
  // Get unit preference
  const { isMetric } = useUnitsPreference();
  
  // Calculate visible column count for dynamic table layout
  const visibleColumnCount = useMemo(() => {
    // Count visible columns + checkbox column + actions column
    return Array.from(visibleColumns).length + 2;
  }, [visibleColumns]);
  
  // Determine if table should stretch or scroll
  // Stretch when 10 or fewer columns (including checkbox and actions), scroll otherwise
  const shouldStretchTable = visibleColumnCount <= 10;
  
  // Memoize expensive filtering operation
  const displayPlayers = useMemo(() => {
    const hasActiveFilters = Object.keys(filters).some(key => filters[key] && filters[key].trim() !== '');
    const shouldFilter = hasActiveFilters || !includeMissingValues;
    return shouldFilter ? filterPlayers(allPlayers) : allPlayers;
  }, [allPlayers, filterPlayers, filters, includeMissingValues]);
  
  // Notify parent about filtered player count
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(displayPlayers.length);
    }
  }, [displayPlayers.length, onFilteredCountChange]);
  
  // Shortlists are now provided by data coordinator
  
  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Apply sorting to players - memoized for performance
  const sortedPlayers = useMemo(() => {
    return [...displayPlayers].sort((a, b) => {
    if (!sortField) return 0;
    
    // Special handling for position field as it's now an array
    if (sortField === 'position') {
      const aPosition = a.position && a.position.length > 0 ? a.position.join(', ') : 'Unknown';
      const bPosition = b.position && b.position.length > 0 ? b.position.join(', ') : 'Unknown';
      
      return sortDirection === 'asc'
        ? aPosition.localeCompare(bPosition)
        : bPosition.localeCompare(aPosition);
    }
    
    // Handle different field types
    const aValue = a[sortField as keyof Player];
    const bValue = b[sortField as keyof Player];
    
    // Handle dates
    if (sortField === 'dateAdded' || sortField === 'dateUpdated') {
      return sortDirection === 'asc' 
        ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
        : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
    }
    
    // Handle numbers
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle strings
    return sortDirection === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
    });
  }, [displayPlayers, sortField, sortDirection]);
  
  // Pagination - memoized for performance
  const { totalPages, paginatedPlayers } = useMemo(() => {
    const total = Math.ceil(sortedPlayers.length / playersPerPage);
    const paginated = sortedPlayers.slice(
      (currentPage - 1) * playersPerPage,
      currentPage * playersPerPage
    );
    return { totalPages: total, paginatedPlayers: paginated };
  }, [sortedPlayers, currentPage, playersPerPage]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  // Note: formatDate function removed as it's not currently used
  
  // Bulk add to shortlist mutation (reused for single add)
  const bulkAddToShortlistMutation = useMutation({
    mutationFn: async ({ shortlistId, playerIds }: { shortlistId: number, playerIds: number[] }) => {
      const response = await apiRequest(
        "POST",
        `/api/shortlists/${shortlistId}/players/bulk`,
        { playerIds }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to add players to shortlist");
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      const { added_count, skipped_count, errors } = data;
      let message = `Added ${added_count} player${added_count === 1 ? '' : 's'} to shortlist`;
      if (skipped_count > 0) {
        message += `, ${skipped_count} already in shortlist`;
      }
      if (errors.length > 0) {
        message += `, ${errors.length} errors`;
      }
      toast({
        title: "Add to shortlist",
        description: message,
      });
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      if (variables && variables.shortlistId) {
        queryClient.invalidateQueries({ queryKey: [`/api/shortlists/${variables.shortlistId}`] });
      }
      setShortlistMenuOpen(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add player to shortlist",
        variant: "destructive",
      });
      setShortlistMenuOpen(null);
    },
  });

  // Handle add to shortlist (single)
  const handleAddToShortlist = (shortlistId: number, playerId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the player modal
    bulkAddToShortlistMutation.mutate({ shortlistId, playerIds: [playerId] });
  };
  
  // Handle player click to open modal
  const handlePlayerClick = async (player: Player) => {
    // Fetch the latest player data to ensure we have the most up-to-date notes
    try {
      const response = await apiRequest("GET", `/api/players/${player.id}`);
      if (response.ok) {
        const updatedPlayer = await response.json();
        setSelectedPlayer(updatedPlayer);
      } else {
        // If fetch fails, use the player data we already have
        setSelectedPlayer(player);
      }
    } catch (error) {
      console.error("Failed to fetch latest player data:", error);
      setSelectedPlayer(player);
    }
    setShowDetailModal(true);
  };
  
  // Handle delete button click
  const handleDeleteClick = (player: Player, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the player modal
    setPlayerToDelete(player);
    setShowDeleteDialog(true);
  };
  
  // Shared selection event handlers for checkbox integration
  const handleSelectPlayerChange = (playerId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent opening player modal
    handleSelectPlayer(playerId, event.target.checked);
  };

  const handleSelectAllCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const currentPagePlayerIds = paginatedPlayers.map(p => p.id);
    handleSelectAll(currentPagePlayerIds, event.target.checked);
  };

  // Column customization handlers
  const handleToggleColumn = (columnKey: string) => {
    const newVisibleColumns = new Set(visibleColumns);
    if (newVisibleColumns.has(columnKey)) {
      // Don't allow hiding always-visible columns
      const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
      if (column?.alwaysVisible) return;
      
      newVisibleColumns.delete(columnKey);
      
      // When hiding a column, remove any related filters
      const filtersToRemove = getFiltersForColumn(columnKey);
      if (filtersToRemove.length > 0 && onFilterRemoval) {
        onFilterRemoval(filtersToRemove);
      }
    } else {
      newVisibleColumns.add(columnKey);
    }
    setVisibleColumns(newVisibleColumns);
  };

  // Helper function to map column keys to their related filter keys
  const getFiltersForColumn = (columnKey: string): string[] => {
    const filterKeys: Record<string, string[]> = {
      'name': ['name'],
      'position': ['position', 'positions'],
      'specificPositions': ['specificPositions'],
      'nationality': ['nationality', 'nationalities'],
      'playingLevel': ['playing_level', 'playingLevels'],
      'event': ['event', 'events'],
      'age': ['ageMin', 'ageMax'],
      'height': ['heightMin', 'heightMax', 'heightUnit'],
      'weight': ['weightMin', 'weightMax', 'weightUnit'],
      'gpa': ['gpaMin', 'gpaMax'],
      'hsGradYear': ['hsGradYearMin', 'hsGradYearMax'],
      'internationalCaps': ['internationalCaps'],
      'currentClub': ['current_club']
    };
    
    return filterKeys[columnKey] || [];
  };

  const handleResetColumns = () => {
    const defaultColumns = new Set(AVAILABLE_COLUMNS.filter(col => col.defaultVisible).map(col => col.key));
    setVisibleColumns(defaultColumns);
    
    // When resetting columns, also clear any filters for columns that are being hidden
    const hiddenColumns = AVAILABLE_COLUMNS
      .filter(col => !defaultColumns.has(col.key))
      .map(col => col.key);
    
    const allFiltersToRemove: string[] = [];
    hiddenColumns.forEach(columnKey => {
      const filtersToRemove = getFiltersForColumn(columnKey);
      allFiltersToRemove.push(...filtersToRemove);
    });
    
    if (allFiltersToRemove.length > 0 && onFilterRemoval) {
      onFilterRemoval(allFiltersToRemove);
    }
  };

  // CSV Export using shared utility
  const handleExportCSV = () => {
    exportPlayersToCSV({
      players: displayPlayers,
      visibleColumns,
      columnConfigs: AVAILABLE_COLUMNS,
      isMetric,
      filename: generateCSVFilename('players'),
      toastFunction: toast
    });
  };

  // Helper function to render table headers
  const renderTableHeader = (columnKey: string) => {
    const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
    if (!column || !visibleColumns.has(columnKey)) return null;

    return (
      <th key={columnKey} scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
        <button 
          className="flex items-center focus:outline-none"
          onClick={() => handleSort(columnKey)}
        >
          {column.label}
          <i className={`fas fa-sort${sortField === columnKey ? (sortDirection === 'asc' ? '-up' : '-down') : ''} ml-1 text-neutral-400`}></i>
        </button>
      </th>
    );
  };

  // Helper function to render table cells
  const renderTableCell = (player: Player, columnKey: string) => {
    if (!visibleColumns.has(columnKey)) return null;

    const getCellContent = () => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex items-center">
              <AvatarPlaceholder name={player.name} className="flex-shrink-0 h-10 w-10" />
              <div className="ml-4">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-neutral-900">{player.name}</div>
                  {player.has_sent_email && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      <i className="fas fa-envelope mr-1"></i>
                      Emailed
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        case 'age':
          return <div className="text-sm text-neutral-900">{player.age}</div>;
        case 'position':
          return <div className="text-sm text-neutral-900">{player.position && player.position.length > 0 ? player.position.join(', ') : 'Unknown'}</div>;
        case 'specificPositions':
          return (
            <div className="text-sm text-neutral-900">
              {player.specific_positions && player.specific_positions.length > 0
                ? player.specific_positions.join(', ')
                : 'N/A'}
            </div>
          );
        case 'height':
          return <div className="text-sm text-neutral-900">{convertHeight(player.height, isMetric)}</div>;
        case 'weight':
          return <div className="text-sm text-neutral-900">{convertWeight(player.weight, isMetric)}</div>;
        case 'playingLevel':
          return (
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              player.playing_level === 'D1' ? 'bg-green-100 text-green-800' :
              player.playing_level === 'D2' ? 'bg-blue-100 text-blue-800' :
              player.playing_level === 'Professional Academy' ? 'bg-purple-100 text-purple-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {player.playing_level || 'N/A'}
            </span>
          );
        case 'gpa':
          return <div className="text-sm text-neutral-900">{player.gpa || 'N/A'}</div>;
        case 'hsGradYear':
          return <div className="text-sm text-neutral-900">{player.hs_grad_year || 'N/A'}</div>;
        case 'currentClub':
          return <div className="text-sm text-neutral-900">{player.current_club || 'N/A'}</div>;
        case 'nationality':
          return <div className="text-sm text-neutral-900">{player.nationality || 'N/A'}</div>;
        case 'internationalCaps':
          return (
            <div className="text-sm text-neutral-900">
              {player.international_caps && player.international_caps.length > 0
                ? player.international_caps
                    .filter((cap) => cap && typeof cap === 'object') // Filter out invalid cap objects
                    .map((cap, index) => {
                      // Handle the actual data structure: [{"U15":"3"},{"U16":"3"}]
                      return Object.entries(cap).map(([level, caps]) => `${level}: ${caps}`);
                    })
                    .flat() // Flatten the array since we're mapping over entries
                    .join(', ')
                : 'None'}
            </div>
          );
        case 'previousClubs':
          return (
            <div className="text-sm text-neutral-900">
              {player.previous_clubs && player.previous_clubs.length > 0
                ? player.previous_clubs.join(', ')
                : 'N/A'}
            </div>
          );
        case 'universityGradYear':
          return <div className="text-sm text-neutral-900">{player.university_grad_year || 'N/A'}</div>;
        case 'jerseyNumber':
          return <div className="text-sm text-neutral-900">{player.jersey_number || 'N/A'}</div>;
        case 'hsGradYearInEmail':
          return (
            <div className="text-sm text-neutral-900">
              {player.hs_grad_year_in_email === true ? (
                <span className="text-green-600 font-medium">Yes</span>
              ) : player.hs_grad_year_in_email === false ? (
                <span className="text-red-600 font-medium">No</span>
              ) : (
                'N/A'
              )}
            </div>
          );
        case 'universityGradYearInEmail':
          return (
            <div className="text-sm text-neutral-900">
              {player.university_grad_year_in_email === true ? (
                <span className="text-green-600 font-medium">Yes</span>
              ) : player.university_grad_year_in_email === false ? (
                <span className="text-red-600 font-medium">No</span>
              ) : (
                'N/A'
              )}
            </div>
          );
        case 'event':
          return (
            <div className="text-sm text-neutral-900">
              {player.event && player.event.length > 0 ? player.event.join(', ') : 'N/A'}
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <td key={columnKey} className="px-6 py-4 whitespace-nowrap">
        {getCellContent()}
      </td>
    );
  };

  // Show skeleton loading during initial load
  if (isInitialLoading) {
    return <PlayerTableSkeleton />;
  }

  return (
    <>
      {/* Bulk Actions Bar */}
      {getSelectionCount() > 0 && (
        <BulkActionsBar
          selectedPlayerIds={Array.from(selectedPlayerIds)}
          totalPlayers={displayPlayers.length}
          onClearSelection={handleClearSelection}
          shortlists={shortlists}
          players={displayPlayers.filter(p => selectedPlayerIds.has(p.id))}
        />
      )}
      
      {/* Selection Controls */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 w-full max-w-full overflow-hidden">
        <div className="flex items-center gap-4 flex-wrap min-w-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSelectAllFiltered(displayPlayers.map(p => p.id))}
              className="text-sm font-medium flex items-center gap-2 px-3 py-1 rounded border border-primary text-primary hover:bg-primary hover:text-white transition-colors flex-shrink-0"
            >
              <i className="fas fa-check-square"></i>
              Select all {displayPlayers.length} players
            </button>
          </div>
          
          {getSelectionCount() > 0 && (
            <span className="text-sm text-neutral-600 flex-shrink-0">
              {getSelectionCount()} selected
            </span>
          )}
          
          {getSelectionCount() > 0 && (
            <button
              onClick={handleClearSelection}
              className="text-sm text-neutral-600 hover:text-neutral-800 flex-shrink-0"
            >
              Clear selection
            </button>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowColumnCustomizer(true)}
            className="text-sm font-medium flex items-center justify-center gap-2 px-3 py-2 rounded border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors whitespace-nowrap"
          >
            <i className="fas fa-columns"></i>
            <span className="hidden xs:inline">Columns</span>
            <span className="xs:hidden">Columns</span>
          </button>
          
          <button
            onClick={handleExportCSV}
            className="text-sm font-medium flex items-center justify-center gap-2 px-3 py-2 rounded border border-green-600 text-green-600 hover:bg-green-600 hover:text-white transition-colors"
          >
            <i className="fas fa-file-csv"></i>
            <span className="hidden sm:inline">Export Displaying Players to CSV</span>
            <span className="sm:hidden">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Player Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        {sortedPlayers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-neutral-400 mb-2">
              <i className="fas fa-users text-3xl"></i>
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-1">No players found</h3>
            <p className="text-neutral-600">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            {/* Selection Controls */}
            <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={areAllSelected(paginatedPlayers.map(p => p.id))}
                      ref={(el) => {
                        const currentPagePlayerIds = paginatedPlayers.map(p => p.id);
                        if (el) el.indeterminate = areSomeSelected(currentPagePlayerIds) && !areAllSelected(currentPagePlayerIds);
                      }}
                      onChange={handleSelectAllCurrentPage}
                      className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-neutral-600">
                      Select all {paginatedPlayers.length} on this page
                    </span>
                  </div>
                  
                  {getSelectionCount() > 0 && (
                    <span className="text-sm text-neutral-600">
                      {getSelectionCount()} selected
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Select</span>
                    </th>
                    {renderTableHeader('name')}
                    {renderTableHeader('age')}
                    {renderTableHeader('position')}
                    {renderTableHeader('specificPositions')}
                    {renderTableHeader('height')}
                    {renderTableHeader('weight')}
                    {renderTableHeader('playingLevel')}
                    {renderTableHeader('gpa')}
                    {renderTableHeader('hsGradYear')}
                    {renderTableHeader('currentClub')}
                    {renderTableHeader('nationality')}
                    {renderTableHeader('internationalCaps')}
                    {renderTableHeader('previousClubs')}
                    {renderTableHeader('universityGradYear')}
                    {renderTableHeader('jerseyNumber')}
                    {renderTableHeader('hsGradYearInEmail')}
                    {renderTableHeader('universityGradYearInEmail')}
                    {renderTableHeader('event')}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {paginatedPlayers.map((player) => (
                    <tr 
                      key={player.id} 
                      className="hover:bg-neutral-50 cursor-pointer" 
                      onClick={() => handlePlayerClick(player)}
                    >
                      <td className="relative px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
                          checked={isPlayerSelected(player.id)}
                          onChange={(e) => handleSelectPlayerChange(player.id, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      {renderTableCell(player, 'name')}
                      {renderTableCell(player, 'age')}
                      {renderTableCell(player, 'position')}
                      {renderTableCell(player, 'specificPositions')}
                      {renderTableCell(player, 'height')}
                      {renderTableCell(player, 'weight')}
                      {renderTableCell(player, 'playingLevel')}
                      {renderTableCell(player, 'gpa')}
                      {renderTableCell(player, 'hsGradYear')}
                      {renderTableCell(player, 'currentClub')}
                      {renderTableCell(player, 'nationality')}
                      {renderTableCell(player, 'internationalCaps')}
                      {renderTableCell(player, 'previousClubs')}
                      {renderTableCell(player, 'universityGradYear')}
                      {renderTableCell(player, 'jerseyNumber')}
                      {renderTableCell(player, 'hsGradYearInEmail')}
                      {renderTableCell(player, 'universityGradYearInEmail')}
                      {renderTableCell(player, 'event')}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative inline-block text-left">
                          <div className="flex space-x-2 justify-end">
                            {/* Shortlist button - always clickable */}
                            <button
                              className="text-primary hover:text-primary/80 cursor-pointer focus:outline-none"
                              onClick={e => {
                                e.stopPropagation();
                                setShortlistMenuOpen(shortlistMenuOpen === player.id ? null : player.id);
                              }}
                              title="Add to shortlist"
                            >
                              <i className="fas fa-star"></i>
                            </button>
                            {/* Email button - opens email modal */}
                            <button
                              className="text-primary hover:text-primary/80 cursor-pointer focus:outline-none"
                              onClick={e => {
                                e.stopPropagation();
                                setEmailModalPlayer(player);
                              }}
                              title="Email player"
                            >
                              <i className="fas fa-envelope"></i>
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(player, e)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title="Remove player"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="bg-white px-6 py-4 border-t border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-700">
                    Showing <span className="font-medium">{(currentPage - 1) * playersPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * playersPerPage, sortedPlayers.length)}
                    </span>{' '}
                    of <span className="font-medium">{sortedPlayers.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 bg-white text-sm font-medium ${
                        currentPage === 1
                          ? 'text-neutral-300 cursor-not-allowed'
                          : 'text-neutral-500 hover:bg-neutral-50'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <i className="fas fa-chevron-left text-xs"></i>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 7) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 4) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNumber = totalPages - 6 + i;
                      } else {
                        pageNumber = currentPage - 3 + i;
                      }
                      
                      if (pageNumber === currentPage) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-primary bg-primary text-sm font-medium text-white"
                          >
                            {pageNumber}
                          </span>
                        );
                      }
                      
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className="relative inline-flex items-center px-4 py-2 border border-neutral-300 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium ${
                        currentPage === totalPages
                          ? 'text-neutral-300 cursor-not-allowed'
                          : 'text-neutral-500 hover:bg-neutral-50'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <i className="fas fa-chevron-right text-xs"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Player detail modal */}
      {selectedPlayer && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">Loading player details...</div>
        </div>}>
          <PlayerDetailModal
            player={selectedPlayer}
            isOpen={showDetailModal}
            onClose={() => setShowDetailModal(false)}
            shortlists={shortlists}
          />
        </Suspense>
      )}
      
      {/* Column Customizer Modal */}
      <Dialog open={showColumnCustomizer} onOpenChange={setShowColumnCustomizer}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Columns</DialogTitle>
            <DialogDescription>
              Choose which columns to display in the player table. You can always change this later.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-3 py-4 max-h-96 overflow-y-auto">
            {AVAILABLE_COLUMNS.map((column) => (
              <div key={column.key} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id={`column-${column.key}`}
                  checked={visibleColumns.has(column.key)}
                  onChange={() => handleToggleColumn(column.key)}
                  disabled={column.alwaysVisible}
                  className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label 
                  htmlFor={`column-${column.key}`}
                  className={`text-sm font-medium ${column.alwaysVisible ? 'text-neutral-400' : 'text-neutral-900 cursor-pointer'}`}
                >
                  {column.label}
                  {column.alwaysVisible && (
                    <span className="text-xs text-neutral-400 ml-1">(always visible)</span>
                  )}
                </label>
              </div>
            ))}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleResetColumns}
            >
              Reset to Default
            </Button>
            <Button
              onClick={() => setShowColumnCustomizer(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Player Deletion Dialog */}
      {playerToDelete && (
        <PlayerDeletionDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          players={[playerToDelete]}
          onConfirm={(reason) => {
            removePlayer({
              playerId: playerToDelete.id,
              reason,
            });
            setShowDeleteDialog(false);
            setPlayerToDelete(null);
          }}
          isLoading={isRemoving}
        />
      )}

      {/* Email Player Modal */}
      {emailModalPlayer && (
        <PlayerEmailModal
          isOpen={!!emailModalPlayer}
          onClose={() => setEmailModalPlayer(null)}
          player={emailModalPlayer}
        />
      )}
      {/* Single Add to Existing Shortlist Dialog */}
      {singleAddToShortlistPlayer && (
        <BulkActionsBar
          selectedPlayerIds={[singleAddToShortlistPlayer.id]}
          totalPlayers={1}
          onClearSelection={() => setSingleAddToShortlistPlayer(null)}
          shortlists={shortlists}
          players={[singleAddToShortlistPlayer]}
        />
      )}
    </>
  );
}
